import $populateMarkdown from './populateMarkdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { LexicalEditor } from 'lexical';
import { useEffect, useRef } from 'react';

type DOMSelectionOffsets = {
  anchor: number;
  focus: number;
};

type LinkFocusTarget = {
  href: string;
  ordinal: number;
  textContent: string;
};

type Props = {
  markdown: string;
};

const getDOMSelectionOffsets = (rootElement: HTMLElement | null): DOMSelectionOffsets | null => {
  const selection = window.getSelection();
  if (
    !rootElement ||
    !selection?.anchorNode ||
    !selection.focusNode ||
    !rootElement.contains(selection.anchorNode) ||
    !rootElement.contains(selection.focusNode)
  ) {
    return null;
  }

  const getOffset = (node: Node, offset: number) => {
    const range = document.createRange();
    range.setStart(rootElement, 0);
    range.setEnd(node, offset);
    return range.toString().length;
  };

  return {
    anchor: getOffset(selection.anchorNode, selection.anchorOffset),
    focus: getOffset(selection.focusNode, selection.focusOffset),
  };
};

const getDOMTextPointAtOffset = (rootElement: HTMLElement, offset: number): [Text, number] | null => {
  const textNodes = document.createTreeWalker(rootElement, NodeFilter.SHOW_TEXT);
  let remainingOffset = offset;
  let textNode = textNodes.nextNode();
  let lastTextNode: Text | null = null;
  while (textNode) {
    if (!(textNode instanceof Text)) {
      textNode = textNodes.nextNode();
      continue;
    }
    const textLength = textNode.textContent?.length ?? 0;
    if (remainingOffset <= textLength) {
      return [textNode, remainingOffset];
    }
    remainingOffset -= textLength;
    lastTextNode = textNode;
    textNode = textNodes.nextNode();
  }

  return lastTextNode ? [lastTextNode, lastTextNode.textContent?.length ?? 0] : null;
};

const restoreDOMSelection = (rootElement: HTMLElement | null, offsets: DOMSelectionOffsets | null) => {
  if (!rootElement || !offsets) {
    return;
  }

  const anchor = getDOMTextPointAtOffset(rootElement, offsets.anchor);
  const focus = getDOMTextPointAtOffset(rootElement, offsets.focus);
  if (!anchor || !focus) {
    return;
  }

  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  selection.removeAllRanges();
  if (selection.setBaseAndExtent) {
    selection.setBaseAndExtent(anchor[0], anchor[1], focus[0], focus[1]);
    return;
  }

  const range = document.createRange();
  try {
    range.setStart(anchor[0], anchor[1]);
    range.setEnd(focus[0], focus[1]);
  } catch {
    range.setStart(focus[0], focus[1]);
    range.setEnd(anchor[0], anchor[1]);
  }
  selection.addRange(range);
};

const getLinkFocusTarget = (editor: LexicalEditor): LinkFocusTarget | null => {
  const rootElement = editor.getRootElement();
  const activeElement = document.activeElement;
  if (!(activeElement instanceof HTMLAnchorElement) || !rootElement?.contains(activeElement)) {
    return null;
  }

  const ordinal = Array.from(rootElement.querySelectorAll('a')).indexOf(activeElement);
  if (ordinal === -1) {
    return null;
  }

  return { href: activeElement.href, ordinal, textContent: activeElement.textContent ?? '' };
};

const restoreLinkFocus = (editor: LexicalEditor, target: LinkFocusTarget | null) => {
  if (!target) {
    return;
  }

  const link = Array.from(editor.getRootElement()?.querySelectorAll('a') ?? [])[target.ordinal];
  // Preserve the focused structural link; falling back to an identical URL/text match moves keyboard focus backwards.
  if (link?.href === target.href && link.textContent === target.textContent) {
    link.focus();
  }
};

/** Synchronizes changed Markdown snapshots into the existing read-only Lexical editor. */
export const MarkdownRendererSyncPlugin = ({ markdown }: Props) => {
  const [editor] = useLexicalComposerContext();
  const previousMarkdownRef = useRef(markdown);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Lexical only accepts prop changes through its imperative editor.update boundary.
  useEffect(() => {
    if (previousMarkdownRef.current === markdown) {
      return;
    }

    previousMarkdownRef.current = markdown;
    const selectionOffsets = getDOMSelectionOffsets(editor.getRootElement());
    const linkFocusTarget = getLinkFocusTarget(editor);
    editor.update(
      () => {
        $populateMarkdown(markdown);
      },
      {
        discrete: true,
        onUpdate: () => {
          restoreLinkFocus(editor, linkFocusTarget);
          restoreDOMSelection(editor.getRootElement(), selectionOffsets);
        },
      }
    );
  }, [editor, markdown]);

  return null;
};
